/* eslint-disable max-classes-per-file */
import { Model } from 'app/model/backendmodel';
import { Injectable } from '@angular/core';
import { ExecuteSSISPackageActivity } from 'app/model/pipeline/ssis.activity';
import { LoggerService } from 'app/logger/logger.service';
import { Owned } from 'app/model/base/named';
import { Constants } from 'app/shared/shared-constants';
import { format } from 'app/shared/utils/string.util';

import { ExecuteSSISPackageActivityMetadataOperationService } from './ssis-activity-metadata-operation.service';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { isExpressionObject } from 'app/shared/utils/expression.util';
import { BehaviorSubject } from 'rxjs';
import { ExecuteSSISPackageConnectionManagerParameterConfig, ExecuteSSISPackageParameterConfig } from 'shell-models-lib';

class MetadataNotFoundError extends Error {
  public message: string;
  public status: number;
  constructor(message: string) {
    super();
    this.message = message;
    this.status = 404;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ExecuteSSISPackageActivityMetadataService {

  public static readonly INIT_STATUS_SUCCESS = 'success';
  public static readonly INIT_STATUS_SKIPPED = 'skipped';
  public static readonly INIT_STATUS_SKIPPED_FOR_IR_NOT_STARTED = 'skippedForIrNotStarted';
  public static readonly INIT_STATUS_FAILED = 'failed';

  private static readonly componentName = 'ExecuteSSISPackageActivityMetadataService';

  public clearErrors$ = new BehaviorSubject<boolean>(false);

  constructor(private readonly operationsService: ExecuteSSISPackageActivityMetadataOperationService,
    private readonly logger: LoggerService) {}

  public static findSsisFolderFromCache(irName: string, folderName: string): Model.SsisFolder {
    return ExecuteSSISPackageActivityMetadataOperationService.findSsisFolderFromCache(irName, folderName);
  }

  public static findSsisProjectWithoutParameterFromCache(irName: string, folderName: string, projectName: string): Model.SsisProject {
    return ExecuteSSISPackageActivityMetadataOperationService.findSsisProjectWithoutParameterFromCache(irName, folderName, projectName);
  }

  public static findSsisProjectWithParameterFromCache(irName: string, folderName: string, projectName: string): Model.SsisProject {
    return ExecuteSSISPackageActivityMetadataOperationService.findSsisProjectWithParameterFromCache(irName, folderName, projectName);
  }

  public static findSsisPackageFromCache(irName: string, folderName: string, projectName: string, packageName: string): Model.SsisPackage {
    return ExecuteSSISPackageActivityMetadataOperationService.findSsisPackageFromCache(irName, folderName, projectName, packageName);
  }

  public static findSsisEnvironmentFromCache(irName: string, folderName: string, environmentPath: string): Model.SsisEnvironment {
    return ExecuteSSISPackageActivityMetadataOperationService.findSsisEnvironmentFromCache(irName, folderName, environmentPath);
  }

  public static isIrUnderRefreshing(irName: string): boolean {
    return ExecuteSSISPackageActivityMetadataOperationService.isIrUnderRefreshing(irName);
  }

  public async isIrStartedAsync(irName: string): Promise<boolean> {
    return this.operationsService.isIrStartedAsync(irName);
  }

  public async refreshMetadataAsync(irName: string): Promise<void> {
    this.operationsService.refreshMetadataAsync(irName);
  }

  public async fetchSsisFolderListAsync(irName: string): Promise<Model.SsisFolder[]> {
    return this.operationsService.fetchSsisFolderListAsync(irName);
  }

  public async fetchSsisProjectListAsync(irName: string, folderId: number): Promise<Model.SsisProject[]> {
    return this.operationsService.fetchSsisProjectListAsync(irName, folderId);
  }

  public async fetchSsisProjectAsync(irName: string, folderId: number, projectId: number): Promise<Model.SsisProject> {
    return this.operationsService.fetchSsisProjectAsync(irName, folderId, projectId);
  }

  public async fetchSsisPackageListAsync(irName: string, folderId: number, projectId: number): Promise<Model.SsisPackage[]> {
    return this.operationsService.fetchSsisPackageListAsync(irName, folderId, projectId);
  }

  public async fetchSsisEnvironmentListAsync(irName: string, folderId: number): Promise<Model.SsisEnvironment[]> {
    return this.operationsService.fetchSsisEnvironmentListAsync(irName, folderId);
  }

  public async fetchSsisEnvironmentByReferenceAsync(irName: string, folderName: string, envName: string): Promise<Model.SsisEnvironment> {
    if (!folderName || !envName) {
      return null;
    }
    const folders = await this.fetchSsisFolderListAsync(irName);
    const folder = folders.find((f) => f.name === folderName);
    if (!folder) {
      throw new MetadataNotFoundError(format(stringResources.SsisActivityMetadataNotFound, 'Folder', folderName));
    }
    const environments = await this.fetchSsisEnvironmentListAsync(irName, folder.id);
    const environment = environments.find((env) => env.name === envName);
    if (!environment) {
      throw new MetadataNotFoundError(format(stringResources.SsisActivityMetadataNotFound, 'Environment', envName));
    }
    return environment;
  }

  public async initActivityInAutoConfiurationModeAsync(activity: ExecuteSSISPackageActivity): Promise<IInitActivityInAutoConfigurationReturn> {
    if (!activity || !activity.ssisIr) {
      return {
        status: ExecuteSSISPackageActivityMetadataService.INIT_STATUS_SKIPPED
      };
    }
    let selectedFolder: Model.SsisFolder = null;
    let selectedProject: Model.SsisProject = null;
    let selectedPackage: Model.SsisPackage = null;
    let selectedEnvironmentRef: Model.SsisEnvironmentReference = null;
    let selectedEnvironment: Model.SsisEnvironment = null;
    try {
      const irStarted = await this.isIrStartedAsync(activity.ssisIr.name);
      if (!irStarted) {
        return {
          status: ExecuteSSISPackageActivityMetadataService.INIT_STATUS_SKIPPED_FOR_IR_NOT_STARTED
        };
      }
      if (activity.ssisFolder) {
          selectedFolder = await this.fetchSelectedFolderAsync(activity);
        if (activity.ssisProject) {
          selectedProject = await this.fetchSelectedProjectAsync(activity, selectedFolder);
          if (activity.ssisPackage) {
            selectedPackage = await this.fetchSelectedPackageAsync(activity, selectedProject);
          }
          if (activity.ssisEnvironment) {
            selectedEnvironmentRef = await this.fetchSelectedEnvironmentRefAsync(activity, selectedProject);
            const envFolderName = selectedEnvironmentRef.environmentFolderName ? selectedEnvironmentRef.environmentFolderName : selectedFolder.name;
            selectedEnvironment = await this.fetchSsisEnvironmentByReferenceAsync(activity.ssisIr.name, envFolderName, selectedEnvironmentRef.environmentName);
          }
        }
      }

      let removedParameterConfigNumber = 0;
      let removedConnectionManagerConfigNumber = 0;
      if (selectedProject) {
        const removedPjConfigs = this.initActivityParameterConfigs(activity, selectedProject.parameters, ExecuteSSISPackageParameterConfig.SCOPE_PROJECT, selectedEnvironment);
        removedParameterConfigNumber += removedPjConfigs[0];
        removedConnectionManagerConfigNumber += removedPjConfigs[1];
        if (selectedPackage) {
          const removedPkgConfigs = this.initActivityParameterConfigs(activity, selectedPackage.parameters, ExecuteSSISPackageParameterConfig.SCOPE_PACKAGE, selectedEnvironment);
          removedParameterConfigNumber += removedPkgConfigs[0];
          removedConnectionManagerConfigNumber += removedPkgConfigs[1];
        }
        this.orderParameterConfigs(activity.parameters);
        this.orderConnectionManagerParameterConfigs(activity.connectionManagerParameters);
      }
      return {
        status: ExecuteSSISPackageActivityMetadataService.INIT_STATUS_SUCCESS,
        selectedFolder,
        selectedProject,
        selectedPackage,
        selectedEnvironmentRef,
        removedParameterConfigNumber,
        removedConnectionManagerConfigNumber
      };
    } catch (error) {
      this.logger.error(ExecuteSSISPackageActivityMetadataService.componentName, error);
      return {
        selectedFolder,
        selectedProject,
        selectedPackage,
        selectedEnvironmentRef,
        status: ExecuteSSISPackageActivityMetadataService.INIT_STATUS_FAILED,
        error
      };
    }
  }

  public toSsisParameterConfigs(parameters: Model.SsisParameter[], scope: string, owner: Owned, ssisEnvironment: Model.SsisEnvironment = null):
      [ExecuteSSISPackageParameterConfig[], ExecuteSSISPackageConnectionManagerParameterConfig[]] {
    if (!parameters) {
      return [[], []];
    }
    const paramConfigs: ExecuteSSISPackageParameterConfig[] = [];
    const cmParamConfigs: ExecuteSSISPackageConnectionManagerParameterConfig[] = [];

    parameters.forEach((parameter) => {
      const paramConfig = Owned.create<ExecuteSSISPackageParameterConfig>(ExecuteSSISPackageParameterConfig.MODEL, owner);
      this.updateParameterConfigWithoutValue(paramConfig, parameter, scope, false);
      if (this.isConnectionManagerParameter(parameter)) {
        const cmParamConfig = paramConfig as ExecuteSSISPackageConnectionManagerParameterConfig;
        [cmParamConfig.connectionManager, cmParamConfig.name] = this.splitConnectionManagerNameAndParameterName(parameter);
        cmParamConfigs.push(cmParamConfig);
      } else {
        paramConfig.name = parameter.name;
        paramConfigs.push(paramConfig);
      }
      if (parameter.sensitive) {
        // For sensitive data, mask the original value with '**********'
        ExecuteSSISPackageParameterConfig.VALUE.setValue(paramConfig, Constants.maskedCredential);
      } else if (parameter.valueSet && parameter.valueType !== ExecuteSSISPackageActivity.VALUE_TYPE_REFERENCE) {
        // If does not refer to environemnt variable, but set value. we should use default value
        ExecuteSSISPackageParameterConfig.VALUE.setValue(paramConfig, parameter.defaultValue);
      } else {
        // No matter use environment variable or not, we firstly set to use designDefaultValue
        // We will update the value when environment reference is set
        ExecuteSSISPackageParameterConfig.VALUE.setValue(paramConfig, parameter.designDefaultValue);
      }
    });
    this.updateParameterConfigValuesByEnvironment(paramConfigs.concat(cmParamConfigs), ssisEnvironment);
    this.orderParameterConfigs(paramConfigs);
    this.orderConnectionManagerParameterConfigs(cmParamConfigs);
    return [paramConfigs, cmParamConfigs];
  }

  public updateParameterConfigValuesByEnvironment(configs: ExecuteSSISPackageParameterConfig[], environment: Model.SsisEnvironment): void {
    if (!environment) {
      // recover all referred configs to use designDefaultValue, expect sensitive data
      configs.forEach((config) => {
        if (config.valueSet && config.valueType === ExecuteSSISPackageActivity.VALUE_TYPE_REFERENCE) {
          if (config.sensitive) {
            ExecuteSSISPackageParameterConfig.VALUE.setValue(config, Constants.maskedCredential);
          } else {
            ExecuteSSISPackageParameterConfig.VALUE.setValue(config, config.designDefaultValue);
          }
        }
      });
    } else {
      // update all referred configs to use variable
      const variableMap = new Map<string, Model.SsisVariable>();
      if (environment.variables) {
        environment.variables.forEach((variable) => {
          variableMap.set(variable.name, variable);
        });
      }
      configs.forEach((config) => {
        if (config.valueSet && config.valueType === ExecuteSSISPackageActivity.VALUE_TYPE_REFERENCE && variableMap.has(config.referencedVariable)) {
          const referVar = variableMap.get(config.referencedVariable);
          if (referVar.sensitive) {
            // For sensitive data, mask the original value with '**********' and set fromJson to false. Because it should use environment value
            config.fromJson = false;
            ExecuteSSISPackageParameterConfig.VALUE.setValue(config, Constants.maskedCredential);
          } else if (!config.fromJson && !this.isUsingExpression(config)) {
            ExecuteSSISPackageParameterConfig.VALUE.setValue(config, referVar.value);
          }
        }
      });
    }
  }

  private async fetchSelectedFolderAsync(activity: ExecuteSSISPackageActivity): Promise<Model.SsisFolder> {
    const ssisFolders = await this.fetchSsisFolderListAsync(activity.ssisIr.name);
    const ssisFolder = ssisFolders.find((sf) => sf.name === activity.ssisFolder);
    if (!ssisFolder) {
      throw new MetadataNotFoundError(format(stringResources.SsisActivityMetadataNotFound, 'Folder', activity.ssisFolder));
    }
    return ssisFolder;
  }

  private async fetchSelectedProjectAsync(activity: ExecuteSSISPackageActivity, ssisFolder: Model.SsisFolder): Promise<Model.SsisProject> {
    const ssisProjects = await this.fetchSsisProjectListAsync(activity.ssisIr.name, ssisFolder.id);
    const ssisProject = ssisProjects.find((sp) => sp.name === activity.ssisProject);
    if (!ssisProject) {
      throw new MetadataNotFoundError(format(stringResources.SsisActivityMetadataNotFound, 'Project', activity.ssisProject));
    }
    // get project with parameter
    return await this.fetchSsisProjectAsync(activity.ssisIr.name, ssisFolder.id, ssisProject.id);
  }

  private async fetchSelectedPackageAsync(activity: ExecuteSSISPackageActivity, ssisProject: Model.SsisProject): Promise<Model.SsisPackage> {
    const ssisPackages = await this.fetchSsisPackageListAsync(activity.ssisIr.name, ssisProject.folderId, ssisProject.id);
    const ssisPackage = ssisPackages.find((sp) => sp.name === activity.ssisPackage);
    if (!ssisPackage) {
      throw new MetadataNotFoundError(format(stringResources.SsisActivityMetadataNotFound, 'Package', activity.ssisPackage));
    }
    return ssisPackage;
  }

  private async fetchSelectedEnvironmentRefAsync(activity: ExecuteSSISPackageActivity, ssisProject: Model.SsisProject): Promise<Model.SsisEnvironmentReference> {
    const ssisEnvRef = ssisProject.environmentRefs ? ssisProject.environmentRefs.find((envRef) => this.toEnvironmentRefPath(envRef) === activity.ssisEnvironment) : null;
    if (!ssisEnvRef) {
      throw new MetadataNotFoundError(format(stringResources.SsisActivityMetadataNotFound, 'Environment reference', activity.ssisEnvironment));
    }
    return ssisEnvRef;
  }

  private initActivityParameterConfigs(activity: ExecuteSSISPackageActivity, parameters: Model.SsisParameter[], scope: string, ssisEnvironment: Model.SsisEnvironment): number[] {
    if (!parameters) {
      return [0, 0];
    }
    let removedParamNum = 0;
    let removedCmParamNum = 0;

    // Compare parameters in model with fetched from storage
    // 1. If parameter exists in both model and storage, and the value has been specified in json, update its definition only.
    // 2. If parameter exists in both model and storage, but the value is not specified in json, remove it from activity and rebuild it according to storage
    // 2. If parameter exists in model, but not in storage, remove it from activity
    const fixedParamMap: Map<string, Model.SsisParameter> = new Map<string, Model.SsisParameter>();
    const rebuildParams: Model.SsisParameter[] = [];
    parameters.forEach((param) => fixedParamMap.set(scope + '@@' + param.name, param));
    if (activity.parameters) {
      for (let i = activity.parameters.length - 1; i >= 0; i--) {
        const config = activity.parameters[i];
        if (config.scope !== scope) {
          continue;
        }
        const configKey = config.scope + '@@' + config.name;
        if (fixedParamMap.has(configKey) && (config.fromJson || this.isUsingExpression(config))) {
          const fixedParam = fixedParamMap.get(configKey);
          this.updateParameterConfigWithoutValue(config, fixedParam, config.scope, false);
          fixedParamMap.delete(configKey);
        } else {
          if (fixedParamMap.has(configKey)) {
            rebuildParams.push(fixedParamMap.get(configKey));
            fixedParamMap.delete(configKey);
          } else {
            removedParamNum++;
          }
          activity.parameters.splice(i, 1);
        }
      }
    }
    if (activity.connectionManagerParameters) {
      for (let i = activity.connectionManagerParameters.length - 1; i >= 0; i--) {
        const config = activity.connectionManagerParameters[i];
        if (config.scope !== scope) {
          continue;
        }
        const configKey = config.scope + '@@' + 'CM.' + config.connectionManager + '.' + config.name;
        if (fixedParamMap.has(configKey) && (config.fromJson || this.isUsingExpression(config))) {
          const fixedParam = fixedParamMap.get(configKey);
          this.updateParameterConfigWithoutValue(config, fixedParam, config.scope, false);
          fixedParamMap.delete(configKey);
        } else {
          if (fixedParamMap.has(configKey)) {
            rebuildParams.push(fixedParamMap.get(configKey));
            fixedParamMap.delete(configKey);
          } else {
            removedCmParamNum++;
          }
          // Will reset index later
          activity.connectionManagerParameters.splice(i, 1);
        }
      }
    }

    // 3. If parameter does not exist in model, but in storage, or the parameter has been remove because not specified in json. Create a parameter config and add into activity parameter.
    const missedParameters: Model.SsisParameter[] = [];
    fixedParamMap.forEach((param) => missedParameters.push(param));
    const [missedParamConfigs, missedCmParamConfigs] = this.toSsisParameterConfigs(missedParameters, scope, activity, ssisEnvironment);
    activity.parameters = activity.parameters ? activity.parameters.concat(...missedParamConfigs) : missedParamConfigs;
    activity.connectionManagerParameters = activity.connectionManagerParameters ? activity.connectionManagerParameters.concat(...missedCmParamConfigs) : missedCmParamConfigs;

    const [rebuildParamConfigs, rebuildCmParamConfigs] = this.toSsisParameterConfigs(rebuildParams, scope, activity, ssisEnvironment);
    activity.parameters = activity.parameters ? activity.parameters.concat(...rebuildParamConfigs) : rebuildParamConfigs;
    activity.connectionManagerParameters = activity.connectionManagerParameters ? activity.connectionManagerParameters.concat(...rebuildCmParamConfigs) : rebuildCmParamConfigs;

    this.changeNullToEmptyString(activity.parameters.concat(activity.connectionManagerParameters));
    this.orderParameterConfigs(activity.parameters);
    this.orderConnectionManagerParameterConfigs(activity.connectionManagerParameters);

    return [removedParamNum, removedCmParamNum];
  }

  private updateParameterConfigWithoutValue(config: ExecuteSSISPackageParameterConfig, parameter: Model.SsisParameter, scope: string, deletable: boolean): void {
    config.scope = scope;
    config.deletable = deletable;
    config.valueSet = parameter.valueSet;
    config.referencedVariable = parameter.variable;
    config.designDefaultValue = parameter.designDefaultValue;
    config.defaultValue = parameter.defaultValue;
    config.valueType = parameter.valueType;
    config.sensitive = parameter.sensitive;
    config.type = parameter.dataType;
    config.required = parameter.required;
  }

  private isConnectionManagerParameter(parameter: Model.SsisParameter): boolean {
    const name = parameter.name;
    if (name && name.startsWith('CM.')) {
      return true;
    }
    return false;
  }

  private isUsingExpression(config: ExecuteSSISPackageParameterConfig): boolean {
    const configValue = ValueAdapter.toResource(config, ExecuteSSISPackageParameterConfig.VALUE);
    return configValue != null && isExpressionObject(configValue);
  }

  private splitConnectionManagerNameAndParameterName(parameter: Model.SsisParameter): [string, string] {
    const firstDotIndex = parameter.name.indexOf('.');
    const lastDotIndex = parameter.name.lastIndexOf('.');
    return [parameter.name.substring(firstDotIndex + 1, lastDotIndex), parameter.name.substring(lastDotIndex + 1)];
  }

  private orderParameterConfigs(configs: ExecuteSSISPackageParameterConfig[]): void {
    if (!configs) {
      return;
    }
    configs.sort((config1, config2) => {
      if (config1.scope === config2.scope) {
        return config1.name > config2.name ? 1 : -1;
      } else {
        return config1.scope === ExecuteSSISPackageParameterConfig.SCOPE_PROJECT ? -1 : 1;
      }
    });
  }

  private orderConnectionManagerParameterConfigs(configs: ExecuteSSISPackageConnectionManagerParameterConfig[]): void {
    if (!configs) {
      return;
    }
    configs.sort((config1, config2) => {
      if (config1.scope === config2.scope) {
        if (config1.connectionManager === config2.connectionManager) {
          return config1.name > config2.name ? 1 : -1;
        } else {
          return config1.connectionManager > config2.connectionManager ? 1 : -1;
        }
      } else {
        return config1.scope === ExecuteSSISPackageParameterConfig.SCOPE_PROJECT ? -1 : 1;
      }
    });

    // reset index
    let prevCm: string = null;
    let index = 0;
    configs.forEach((config) => {
      if (config.connectionManager === prevCm) {
        config.index = ++index;
      } else {
        config.index = 0;
        index = 0;
        prevCm = config.connectionManager;
      }
    });
  }

  private changeNullToEmptyString(configs: ExecuteSSISPackageParameterConfig[]): void {
    if (!configs) {
      return;
    }
    configs.forEach((config) => {
      // value equals null triggers unexpected logic in autosize-textares. Set it to empty string if null
      const val = ValueAdapter.toResource(config, ExecuteSSISPackageParameterConfig.VALUE);
      if (val == null) {
        ExecuteSSISPackageParameterConfig.VALUE.setValue(config, '');
      }
    });
  }

  private toEnvironmentRefPath(envRef: Model.SsisEnvironmentReference): string {
    const folderName = envRef.environmentFolderName ? envRef.environmentFolderName : '.';
    return folderName + '/' + envRef.environmentName;
  }
}

export interface IInitActivityInAutoConfigurationReturn {
  status: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any;
  selectedFolder?: Model.SsisFolder;
  selectedProject?: Model.SsisProject;
  selectedPackage?: Model.SsisPackage;
  selectedEnvironmentRef?: Model.SsisEnvironmentReference;
  removedParameterConfigNumber?: number;
  removedConnectionManagerConfigNumber?: number;
}
